import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import InlinePayOptions from "./InlinePayOptions";
import SpotlightCheckoutBox from "./SpotlightCheckoutBox";
import { ordersApi } from "../services/orders";
import { paymentsApi } from "../services/payments";
import { currencySymbol, formatAmountForCopy, formatSingleAmount } from "../utils/currency";
import { SUFFIX_CUR } from "../utils/requestsFormat";
import { STRIPE_PK, stripePromise, sanitizeMoney } from "../lib/fanCheckout";
import { Capacitor } from "@capacitor/core";
import { IS_NATIVE } from "../lib/stripeFlow";
import { bidStepFor, maxPriceFor } from "../utils/payinConfig";
import type { SongRequest } from "../hooks/useSongRequest";
import { revealAboveKeyboard } from "../lib/revealAboveKeyboard";
import "./SongCheckout.css";
import { t } from "../i18n";
import { T } from "../i18n/T";

// Fans in the iPhone app pay with our Apple Pay button (InlinePayOptions).
const IS_IOS_APP = IS_NATIVE && Capacitor.getPlatform() === "ios";

// Paying for the picked song: the message box (its icon is on the selected
// song's card), the hold panel (the fee-inclusive total, the bid (i), the
// hold-only promise), the Place your bid card (bid box, Fast
// Pass pill), the payment reveal (placeholders, Stripe Elements, the local
// mock), and the free-request button.
// Split out of pages/RequestPage.tsx; the state lives in hooks/useSongRequest.

type Props = {
  dj: any;
  displayDj: any;
  isProfilePreview: boolean;
  isFree: boolean;
  minPrice: number;
  djMinBase: number;
  fastPassBase: number;
  tipMode: boolean;
  buyMode: boolean;
  comment: string;
  setComment: (v: string) => void;
  commentEnabled: boolean;
  setCommentEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  emailForLink: string;
  setEmailForLink: (v: string) => void;
  clientNickname: string;
  clientInstagram: string;
  song: SongRequest;
};

/** Fans write ONE short line — it must fit the DJ's request card row and
    never run under the box's own ×. The server tolerates far more (2000),
    the product wants short. */
export const MESSAGE_MAX = 80;

/** The bid box's − / + (founder 10/06: "keep it the exact way we have
    today, just add those"). One tap = one whole currency step
    ($1 · 10 kr · 500 Ft, utils/payinConfig bidStepFor) — snapped to that grid,
    never below the DJ's price, never above the request cap. */
export function stepBid(current: number, dir: 1 | -1, floor: number, cap: number, step: number): number {
  const eps = 1e-9;
  const next = dir === 1
    ? Math.floor(current / step + eps) * step + step
    : Math.ceil(current / step - eps) * step - step;
  return Math.round(Math.min(cap, Math.max(floor, next)) * 100) / 100;
}

// "Place your bid" with the iMessage-style ripple (founder, 2026-10-25): a
// wave runs through the letters, rests, runs again. Each word stays whole
// when a language wraps; screen readers get the plain title (the heading's
// label, the letters are hidden from them); with Reduce Motion nothing
// moves and the wave runs as a green glow (SongCheckout.css).
function graphemes(word: string): string[] {
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: string }) => { segment: (s: string) => Iterable<{ segment: string }> } }).Segmenter;
  return Seg ? Array.from(new Seg(undefined, { granularity: "grapheme" }).segment(word), (g) => g.segment) : Array.from(word);
}
function RippleTitle({ text }: { text: string }) {
  let i = 0;
  return (
    <div className="rq-bid-title rq-ripple" role="heading" aria-level={2} aria-label={text}>
      <span aria-hidden="true">
        {text.split(" ").map((word, w) => (
          <React.Fragment key={w}>
            {w > 0 && " "}
            <span className="rq-ripple-word">
              {graphemes(word).map((ch) => (
                <span key={i} className="rq-ripple-ch" style={{ "--i": i++ } as React.CSSProperties}>
                  {ch}
                </span>
              ))}
            </span>
          </React.Fragment>
        ))}
      </span>
    </div>
  );
}

// The message box: one line saying what it is for (founder: otherwise it is
// a text box with no instructions), focused without the browser's own
// scroll (autoFocus dragged the page under the header) and then seated right
// above the keyboard, capped, with an × that cancels the message and closes
// the box — the request then goes out with no message.
function MessageBox({ comment, setComment, onClose }: { comment: string; setComment: (v: string) => void; onClose: () => void }) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  // A layout effect runs inside the tap that opened the box, so iOS still
  // opens the keyboard for it.
  useLayoutEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
    return revealAboveKeyboard(wrapRef.current);
  }, []);
  return (
    <>
      <label className="rq-comment-label" htmlFor={id}>{t("songCheckout.addAShortNote")}</label>
      <div className="rq-comment-wrap rq-comment-wrap--free" ref={wrapRef}>
        <input
          id={id}
          ref={inputRef}
          className="rq-comment rq-comment--inbid"
          type="text"
          autoComplete="off"
          placeholder={t("songCheckout.typeYourMessage")}
          maxLength={MESSAGE_MAX}
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, MESSAGE_MAX))}
        />
        <button
          type="button"
          className="rq-comment-x"
          aria-label={t("songCheckout.cancelMessage")}
          onClick={() => {
            setComment("");
            onClose();
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12" /><path d="M18 6L6 18" /></svg>
        </button>
      </div>
    </>
  );
}

export default function SongCheckout({
  dj,
  displayDj,
  isProfilePreview,
  isFree,
  minPrice,
  djMinBase,
  fastPassBase,
  tipMode,
  buyMode,
  comment,
  setComment,
  commentEnabled,
  setCommentEnabled,
  emailForLink,
  setEmailForLink,
  clientNickname,
  clientInstagram,
  song,
}: Props) {
  const {
    selectedTrack,
    baseAmount,
    setBaseAmount,
    createdOrderId,
    tier,
    bidInfoOpen,
    setBidInfoOpen,
    bidPlaceholder,
    setBidPlaceholder,
    showPayInline,
    setShowPayInline,
    payInlineLoading,
    piClientSecret,
    setPiClientSecret,
    showCardForm,
    setShowCardForm,
    processing,
    setProcessing,
    payError,
    setPayError,
    bidFocused,
    setBidFocused,
    bidTouched,
    setBidTouched,
    requestBase,
    totalDisplay,
    isFastPassBid,
    rememberOrdered,
    spotlightOn,
    spotlightNameHidden,
    setSpotlightNameHidden,
    spotlightChoice,
  } = song;
  const isReadyToSubmit =
    selectedTrack !== null && (isFree ? true : requestBase >= minPrice);
  const bidCur = (displayDj.currency || "usd").toLowerCase();
  const bidCap = maxPriceFor(bidCur, displayDj?.capsLifted);
  const bumpBid = (dir: 1 | -1) => {
    const next = String(stepBid(requestBase, dir, minPrice, bidCap, bidStepFor(bidCur)));
    setBaseAmount(next);
    setBidPlaceholder(next);
    setBidTouched(true);
  };
  // The talent's own preview shows the hold too: it is what fans see
  // (founder, 1.5.2 b50: "showcase it, it's good-looking").
  const showHold = !isFree && totalDisplay > 0;
  const stepperRef = useRef<HTMLDivElement | null>(null);
  // A typed bid under the talent's price: the pay buttons wait (the bid
  // snaps back to the price when the box is left).
  const bidTooLow = bidFocused && baseAmount.trim() !== "" && Number(baseAmount) < minPrice;
  const holdLabel = formatSingleAmount(totalDisplay, displayDj.currency);
  // The (i) popover hangs under the whole panel; its pointer sits under the
  // middle of the total's half.
  const holdRef = useRef<HTMLDivElement | null>(null);
  const [pointX, setPointX] = useState(24);
  const pointAt = (el: Element | null) => {
    const panel = holdRef.current?.getBoundingClientRect();
    const r = el?.getBoundingClientRect();
    if (panel && r) setPointX(Math.round(r.left + r.width / 2 - panel.left));
  };
  // The message box opens after the song is picked — after the intent (and
  // the order) was created — so what the fan typed goes with the payment's
  // confirm. It never reached the talent before (2026-10-10). So does the
  // Spotlight choice, when the box was shown ("Hide my name" can come late).
  const confirmDetails = { comment, clientNickname, clientInstagram, ...(spotlightChoice ?? {}) };
  // A free request keeps its button off while the page leaves (below). A
  // Back that restores this page from the browser's cache brings that state
  // back with it — turn the button on again (2026-10-10).
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setProcessing(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [setProcessing]);

  // The bid "i" sits beside "Temporary hold"; with no hold panel (a total
  // of 0) it stays in the bid header.
  const bidInfo = (
    <>
      <button
        type="button"
        className="rq-tier-info-btn rq-bid-info-btn"
        onClick={(e) => {
          pointAt(e.currentTarget.closest(".rq-hold-total"));
          setBidInfoOpen((v) => !v);
        }}
        aria-label={t("songCheckout.whatYouLlPay")}
        aria-expanded={bidInfoOpen}
      >
        i
      </button>
      {bidInfoOpen && (
        <div className="rq-tier-info-popover rq-bid-info-popover" role="status">
          <div className="rq-bid-info-row">
            <T k="songCheckout.yourRequestJoinsThe" vars={{ amount: formatAmountForCopy(djMinBase, displayDj.currency) }} tags={{ 0: (c) => <strong>{c}</strong> }} />
          </div>
          <div className="rq-bid-info-row">
            <strong>{formatAmountForCopy(fastPassBase, displayDj.currency)}+</strong> — <span className="rq-bid-info-fast">{t("songCheckout.higherBidsGetPriority")}</span>
          </div>
          <div className="rq-bid-info-fee">
            {t("songCheckout.aSmallFeeIs")}
          </div>
        </div>
      )}
    </>
  );
  return (
    <>
      {/* The message box — opened from the icon beside the selected song's
          "Selected Song" badge (SongSearchFeed), right under that card:
          above the hold panel on a paid request, above the Request button
          on a free one. */}
      {!tipMode && !buyMode && selectedTrack && (!isFree || !isProfilePreview) && displayDj.allowPersonalizedMessages !== false && commentEnabled && (
        <MessageBox comment={comment} setComment={setComment} onClose={() => setCommentEnabled(false)} />
      )}
      {!tipMode && !buyMode && !isFree && selectedTrack && (
        <section className={`rq-paybox rq-paybox--new${bidTooLow ? " rq-paybox--locked" : ""}`}>
          {/* The hold, ABOVE the bid (founder layout, 2026-10-23): the
              fee-inclusive total on the left with the bid "i", the promise
              on the right. The promise says on screen what the server
              actually does: every song-request PaymentIntent is
              capture_method "manual" — authorised now, captured only at
              Mark Played, cancelled if it never is (server
              lib/captureOrder.js + jobs/expireUncapturedAuths.js). It is the
              heart of the App Review 3.1.1 case that a request is a
              real-world service, not digital content — never shorten it
              away. The total and the promise are plain text: the (i)
              already says a small fee covers processing. */}
          {showHold && (
            <div className="rq-pay-summary rq-pay-summary--hold" ref={holdRef} style={{ "--rq-point-x": `${pointX}px` } as React.CSSProperties}>
              <div className={`rq-hold-total${holdLabel.length > 7 ? " rq-hold-total--long" : ""}`}>
                <div className="rq-hold-label">
                  <span>{t("songCheckout.temporaryHold")}</span>
                  {bidInfo}
                </div>
                <div className="rq-hold-amount">{holdLabel}</div>
              </div>
              <span className="rq-hold-divider" aria-hidden />
              <div className="rq-hold-promise">
                <svg className="rq-hold-shield" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 3l7.5 3v5.5c0 4.6-3.2 8.3-7.5 9.5-4.3-1.2-7.5-4.9-7.5-9.5V6L12 3Z" />
                  <path d="M8.6 12.2l2.4 2.4 4.4-4.6" />
                </svg>
                <span className="rq-hold-promise-text">
                  <span className="rq-pay-summary-promise">{t("songCheckout.chargedOnlyIfYour")}</span>
                  <span className="rq-pay-summary-hold">
                    {t("songCheckout.notPlayedYouPay", { amount: formatAmountForCopy(0, displayDj.currency) })}
                  </span>
                </span>
              </div>
            </div>
          )}

          <div className="rq-bid-card rq-bid-card--new">
            <div className="rq-bid-header">
              <RippleTitle text={t("songCheckout.placeYourBid")} />
              {!showHold && bidInfo}
            </div>
            <div className="rq-bid-sub">{t("songCheckout.higherBidsGetMore")}</div>
            {/* One line, one place above the bid box: the minimum while a
                typed bid is under it, else Fast Pass once the bid earns it. */}
            {bidTooLow ? (
              <div className="rq-bid-note rq-bid-note--min" role="alert">
                <T k="songCheckout.minimumBid" vars={{ amount: formatAmountForCopy(djMinBase, displayDj.currency) }} tags={{ 0: (c) => <strong>{c}</strong> }} />
              </div>
            ) : isFastPassBid ? (
              // The two emojis are the founder's pick (2026-10-25), an exception
              // to the no-Apple-emoji rule for this line only.
              <div className="rq-bid-note rq-bid-note--fast" role="status">
                {/* No-break spaces: an emoji never ends up alone on a line. */}
                <span aria-hidden="true">{"⚡\u00a0"}</span>
                {t("songCheckout.fastPassUnlocked")}
                <span aria-hidden="true">{"\u00a0🎉"}</span>
              </div>
            ) : null}
            <div className="rq-bid-stepper" ref={stepperRef}>
              <button
                type="button"
                className="rq-bid-step"
                aria-label={t("songCheckout.lowerBid")}
                disabled={requestBase <= minPrice}
                onClick={() => bumpBid(-1)}
              >
                −
              </button>
              <label className="rq-bid-input-wrap">
                {!SUFFIX_CUR.has((displayDj.currency || "usd").toLowerCase()) && (
                  <span className="rq-bid-cur">{currencySymbol(displayDj.currency)}</span>
                )}
                <input
                  type="text"
                  inputMode="decimal"
                  enterKeyHint="done"
                  // A bid, not an address, card or password: no autofill
                  // suggestions over the keypad.
                  autoComplete="off"
                  className="rq-bid-input"
                  // As wide as the amount, so "$ 2" sits centred in the pill.
                  style={{ width: `${Math.max((baseAmount || bidPlaceholder || String(minPrice)).length, 1) + 0.4}ch` }}
                  value={baseAmount}
                  onFocus={() => {
                    // The bid row and the pay buttons (Apple Pay / Google
                    // Pay, then "+ Add card details") just above the
                    // keyboard, all tappable without scrolling — not as high
                    // as the browser's own reveal takes it (2026-10-26).
                    const pay = stepperRef.current?.closest(".rq-paybox")?.querySelector<HTMLElement>(".rq-add-card") ?? null;
                    revealAboveKeyboard(stepperRef.current, 12, pay);
                    setBidFocused(true);
                    if (baseAmount && baseAmount.trim() !== "") {
                      setBidPlaceholder(baseAmount);
                    }
                    setBaseAmount("");
                  }}
                  onBlur={(e) => {
                    setBidFocused(false);
                    const v = Number(e.target.value);
                    if (!v) {
                      setBaseAmount(bidPlaceholder || String(minPrice));
                    } else if (v < minPrice) {
                      setBaseAmount(String(minPrice));
                      setBidPlaceholder(String(minPrice));
                    } else {
                      setBidPlaceholder(String(v));
                    }
                  }}
                  onChange={(e) => {
                    const val = sanitizeMoney(e.target.value);
                    setBaseAmount(val);
                    if (!bidTouched && val !== "" && Number(val) >= minPrice) {
                      setBidTouched(true);
                    }
                  }}
                  placeholder={bidPlaceholder || String(minPrice)}
                />
                {SUFFIX_CUR.has((displayDj.currency || "usd").toLowerCase()) && (
                  <span className="rq-bid-cur">{currencySymbol(displayDj.currency)}</span>
                )}
              </label>
              <button
                type="button"
                className="rq-bid-step"
                aria-label={t("songCheckout.raiseBid")}
                disabled={requestBase >= bidCap}
                onClick={() => bumpBid(1)}
              >
                +
              </button>
            </div>
          </div>

          {/* Request Spotlight (talent has it on): the fan's name may show on
              their screen; the box opens the choice. Off = nothing here. */}
          {spotlightOn && !isProfilePreview && !isFree && totalDisplay > 0 && (
            <SpotlightCheckoutBox djName={displayDj.name || ""} hidden={spotlightNameHidden} onChange={setSpotlightNameHidden} />
          )}

          {!isProfilePreview && payInlineLoading && !piClientSecret && <PayButtonsPlaceholder loading applePay={!IS_NATIVE} />}
          {/* The talent's own preview: the pay buttons fans see, drawn and
              inert (no order, no payment). In the app fans get the Apple Pay
              button on an iPhone only (InlinePayOptions). */}
          {isProfilePreview && <PayButtonsPlaceholder applePay={!IS_NATIVE || IS_IOS_APP} />}

          {!isProfilePreview && showPayInline && stripePromise && piClientSecret && (
            <Elements
              key={`el-${piClientSecret}-${totalDisplay.toFixed(2)}-${tier}`}
              stripe={stripePromise}
              options={{
                clientSecret: piClientSecret,
                appearance: { theme: "night" },
                locale: "en",
              }}
            >
              <div className="rq-inline-pay rq-inline-pay--inpaybox">
                <InlinePayOptions
                  key={`ipo-${piClientSecret}-${totalDisplay.toFixed(2)}-${tier}`}
                  orderId={createdOrderId}
                  emailForLink={emailForLink}
                  setEmailForLink={setEmailForLink}
                  context="song"
                  trackCheckout
                  djName={displayDj.name}
                  clientSecret={piClientSecret}
                  currency={displayDj.currency}
                  showCardForm={showCardForm}
                  setShowCardForm={setShowCardForm}
                  processing={processing}
                  setProcessing={setProcessing}
                  payError={payError}
                  setPayError={setPayError}
                  selectedTrack={selectedTrack}
                  totalToCharge={totalDisplay}
                  confirmDetails={confirmDetails}
                  onPaymentSuccess={async (piId?: string) => {
                    if (!createdOrderId) return;
                    try {
                      await paymentsApi.confirmIntent(createdOrderId, piId, confirmDetails);
                    } catch (e) {
                      console.error("confirm-intent on RequestPage failed", e);
                    }
                    setPiClientSecret(null);
                    setShowPayInline(false);
                    window.location.href = `/fan/requests?order=${encodeURIComponent(
                      createdOrderId
                    )}`;
                  }}
                />
              </div>
            </Elements>
          )}

          {!isProfilePreview && showPayInline && !STRIPE_PK && createdOrderId && (
            <div className="rq-inline-pay rq-inline-pay--inpaybox" style={{ padding: "16px", textAlign: "center" }}>
              <p style={{ marginBottom: 12, color: "#aaa", fontSize: 14 }}>{t("songCheckout.localDevModeStripe")}</p>
              <button
                style={{
                  background: "#1db954",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "12px 32px",
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: processing ? "not-allowed" : "pointer",
                  opacity: processing ? 0.6 : 1,
                }}
                disabled={processing}
                onClick={async () => {
                  setProcessing(true);
                  try {
                    await paymentsApi.mockPay(createdOrderId);
                    window.location.href = `/fan/requests?order=${encodeURIComponent(createdOrderId)}`;
                  } catch (e: any) {
                    setPayError(e?.response?.data?.msg || t("songCheckout.mockPaymentFailed"));
                    setProcessing(false);
                  }
                }}
              >
                {processing ? t("songCheckout.processing") : t("songCheckout.mockPay", { amount: formatSingleAmount(totalDisplay, displayDj.currency) })}
              </button>
              {payError && <p style={{ color: "red", marginTop: 8 }}>{payError}</p>}
            </div>
          )}

        </section>

      )}

      {!tipMode && !buyMode && isFree && !isProfilePreview ? (
        <button
          onClick={async () => {
            if (!isReadyToSubmit || !selectedTrack) return;
            setProcessing(true);
            let leaving = false;
            try {
              const payload = {
                djSlug: dj.slug,
                searchQuery: selectedTrack.name,
                trackId: selectedTrack.id,
                amount: totalDisplay,
                requestAmount: requestBase,
                comment,
                trackImage: selectedTrack.image,
                trackArtists: selectedTrack.artists.map((a) => a.name),
                trackAlbum: selectedTrack.album,
                isPriority: false,
                isFast: tier === "fast",
                tier,
                orderId: createdOrderId || undefined,
                clientNickname: clientNickname || undefined,
                clientInstagram: clientInstagram || undefined,
              };

              const data = await ordersApi.createFree(payload);
              if (data?.ok && data?.orderId) {
                rememberOrdered(selectedTrack.id);
                // The button stays off until the page is gone: /orders/free
                // doesn't dedupe, and a second tap while the page was
                // leaving made a second request (2026-10-10).
                leaving = true;
                window.location.href = `/fan/requests?order=${encodeURIComponent(data.orderId)}`;
              } else {
                alert(t("songCheckout.somethingWentWrongPlease"));
              }
            } catch (err: any) {
              console.error("Free request error", err);
              alert(err?.response?.data?.msg || t("songCheckout.serverErrorCreatingFree"));
            } finally {
              if (!leaving) setProcessing(false);
            }
          }}
          className={`rq-cta ${isReadyToSubmit ? "on" : "off"}`}
          disabled={!isReadyToSubmit || processing}
        >
          {processing ? t("songCheckout.sending") : t("songCheckout.request")}
        </button>
      ) : null}

      {!tipMode && !buyMode && selectedTrack && !isProfilePreview && (
        <>
          {/* The old "Add a message (optional)" toggle is gone (founder,
              1.4.1): the icon in the selected-song card's corner opens the
              box above the Request button. */}
          {isFree && (
            <p className="rq-fine">
              {t("songCheckout.freeRequestSentTo", { name: displayDj.name ?? "" })}
            </p>
          )}
        </>
      )}
    </>
  );
}

/** The pay buttons' look with no payment behind them: while the real ones
 *  load, and in the talent's own preview. Same wrapper stack as
 *  InlinePayOptions, so the spacing matches the real pay block. */
function PayButtonsPlaceholder({ loading = false, applePay }: { loading?: boolean; applePay: boolean }) {
  return (
    <div
      className={`rq-inline-pay rq-inline-pay--inpaybox${loading ? " rq-pay-loading-placeholders" : " rq-pay-preview"}`}
      {...(loading ? { "aria-busy": true, "aria-label": t("songCheckout.loadingPaymentOptions") } : { "aria-hidden": true })}
    >
      <div className="rq-inline-pay">
        <div className="rq-wallets">
          {/* applePay: the Apple Pay button's look. Never while the app loads
              (its web wallets are off, so nothing may flash one); yes in the
              iPhone app's preview, where fans get our Apple Pay button. */}
          {applePay && (
            <div className="rq-express-vertical">
              <div className="rq-fake-express-slot">
                <span className="rq-fake-express-slot__icon" aria-hidden>
                  <svg width="16" height="20" viewBox="0 0 814 1000" fill="currentColor" focusable="false">
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.3 39.5-164.4 39.5-68.1 0-120.8-60.4-163.7-122.2C37.1 790.2 0 663.3 0 538.5 0 344.3 126.2 227.2 279.1 227.2c68.1 0 124.4 40.7 167.2 40.7 40.2 0 103.2-43.2 190.8-43.2 30.8 0 142.1 2.7 242.1 108.2zM585.1 152.8c35.3-41.9 61.1-99.8 61.1-158.4 0-8.2-.6-16.5-1.9-24.8-58.1 2.4-126.2 38.9-168.1 87.5-32.3 36.8-61.9 94.8-61.9 153.4 0 8.9.7 17.7 2.1 25.8 4.7.4 9.5.6 14.3.6 47.4 0 93.4-24.7 134.5-63.1z" />
                  </svg>
                </span>
                <span className="rq-fake-express-slot__label">{t("songCheckout.pay")}</span>
              </div>
            </div>
          )}
          <div className="rq-add-card" aria-hidden="true">
            {t("songCheckout.addCardDetails")}
          </div>
        </div>
      </div>
    </div>
  );
}
